import classNames from 'classnames'
import React, {
  CSSProperties,
  FC,
  PropsWithChildren,
  ReactNode,
  forwardRef,
  useEffect,
  useRef,
} from 'react'

import { HorizontalScroll as ScrollProps, useHorizontalScroll } from '@hooks/useHorizontalScroll'

import styles from './HorizontalScroll.module.scss'

interface HorizontalScrollProps {
  scrollBehavior?: 'reset' | 'preserve' | number
  innerRef?: React.RefObject<HTMLDivElement | null>
  innerClassName?: string
  containerClassName?: string
  fadeWidth?: string
  shouldRestorePosition?: boolean
  renderAdditionalContent?: (scrollProps: ScrollProps) => ReactNode
  onMouseOver?: (e: React.MouseEvent<HTMLDivElement>) => void
  onMouseLeave?: (e: React.MouseEvent<HTMLDivElement>) => void
  className?: string
}

export const HorizontalScroll = forwardRef<
  HTMLDivElement,
  PropsWithChildren<HorizontalScrollProps>
>((props, ref) => {
  const { innerClassName, containerClassName, fadeWidth = '7rem', shouldRestorePosition } = props
  const { renderAdditionalContent, onMouseOver, onMouseLeave } = props
  const { scrollBehavior = 'reset', children, className } = props

  const lastScrollPosition = useRef<number | null>(null)

  const scrollProps = useHorizontalScroll<HTMLDivElement>({
    shouldRestorePosition,
  })

  const {
    scrollContainerRef,
    scrollContainerInnerRef,
    hasScrolledToEnd,
    hasScrolledToStart,
    isScrollable,
  } = scrollProps

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current

    if (!scrollContainer) {
      return
    }

    const resetScrollPosition = () => {
      if (scrollBehavior === 'reset') {
        scrollContainer.scrollLeft = 0
        lastScrollPosition.current = 0
      } else if (typeof scrollBehavior === 'number') {
        scrollContainer.scrollLeft = scrollBehavior
        lastScrollPosition.current = scrollBehavior
      } else if (scrollBehavior === 'preserve' && lastScrollPosition.current !== null) {
        scrollContainer.scrollLeft = scrollContainerRef.current?.scrollLeft || 0
      }
    }

    resetScrollPosition()

    const resizeObserver = new ResizeObserver(() => {
      resetScrollPosition()
    })

    resizeObserver.observe(scrollContainer)
    if (scrollContainerInnerRef.current) {
      resizeObserver.observe(scrollContainerInnerRef.current)
    }

    return () => {
      resizeObserver.disconnect()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollBehavior, scrollContainerRef, scrollContainerInnerRef])

  return (
    <div
      className={classNames(styles.scrollContainerWrapper, className, {
        [styles.containerScrollable]: isScrollable,
      })}
      onMouseOver={onMouseOver}
      onMouseLeave={onMouseLeave}
      ref={ref}
    >
      <div
        ref={scrollContainerRef}
        className={classNames(styles.scrollContainer, containerClassName)}
      >
        <div
          ref={scrollContainerInnerRef}
          className={classNames(styles.scrollContainerInner, innerClassName)}
        >
          {children}
        </div>
      </div>
      <HorizontalScrollFade.Left
        active={isScrollable && !hasScrolledToStart}
        style={{ width: fadeWidth }}
      />
      <HorizontalScrollFade.Right
        active={isScrollable && !hasScrolledToEnd}
        style={{ width: fadeWidth }}
      />
      {renderAdditionalContent?.(scrollProps)}
    </div>
  )
})

interface HorizontalScrollFadeProps {
  direction?: 'left' | 'right'
  style?: CSSProperties
  className?: string
  active?: boolean
}

export const HorizontalScrollFade: FC<HorizontalScrollFadeProps> & {
  Left: FC<Omit<HorizontalScrollFadeProps, 'direction'>>
  Right: FC<Omit<HorizontalScrollFadeProps, 'direction'>>
} = (props) => {
  const { direction = 'left', style, className, active } = props

  return (
    <div
      style={style}
      className={classNames(styles.scrollContainerFade, styles[direction], className, {
        [styles.active]: active,
      })}
    />
  )
}

HorizontalScrollFade.Left = (props: Omit<HorizontalScrollFadeProps, 'direction'>) => (
  <HorizontalScrollFade {...props} direction='left' />
)

HorizontalScrollFade.Right = (props: Omit<HorizontalScrollFadeProps, 'direction'>) => (
  <HorizontalScrollFade {...props} direction='right' />
)
